/**
 * Čistá doménová logika nad modelem – bez závislosti na databázi,
 * aby šla snadno testovat.
 */
import type {
  BaseEntity,
  ColonyEvent,
  FeedingEvent,
  FeedType,
  HarvestEvent,
  ID,
  ISODate,
  ISODateTime,
  TreatmentEvent,
  VarroaCheckEvent,
} from './model';

export function nowISO(): ISODateTime {
  return new Date().toISOString();
}

export function todayISO(d: Date = new Date()): ISODate {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Doplní id a časové značky k nové entitě. */
export function newEntity<T extends BaseEntity>(
  data: Omit<T, keyof BaseEntity>,
): T {
  const ts = nowISO();
  return {
    ...data,
    id: crypto.randomUUID(),
    createdAt: ts,
    updatedAt: ts,
    deletedAt: null,
  } as T;
}

export function addDays(date: ISODate, days: number): ISODate {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Kolik kg cukru obsahuje 1 kg / 1 l krmiva – aby šlo krmení sčítat v kilogramech cukru.
 * Roztok 3:2 = 60 % cukru (≈ 1,29 kg/l), 1:1 = 50 % (≈ 1,23 kg/l),
 * invertní sirup ≈ 72 % sušiny (≈ 1,35 kg/l), pogača/těsto ≈ 90 % cukru.
 * U „jiného“ krmiva se kg počítají jako kg cukru.
 */
export const FEED_SUGAR: Record<FeedType, { perKg: number; perL: number }> = {
  syrup_3_2: { perKg: 0.6, perL: 0.77 },
  syrup_1_1: { perKg: 0.5, perL: 0.62 },
  invert_syrup: { perKg: 0.72, perL: 0.97 },
  fondant: { perKg: 0.9, perL: 1.2 },
  other: { perKg: 1, perL: 0.77 },
};

/** Množství cukru v krmení na včelstvo (kg). */
export function feedingSugarKg(e: Pick<FeedingEvent, 'amountPerColony' | 'unit' | 'feedType'>): number {
  const f = FEED_SUGAR[e.feedType] ?? FEED_SUGAR.other;
  return e.amountPerColony * (e.unit === 'l' ? f.perL : f.perKg);
}

/** Spad roztočů, který se sčítá jako „celkový spad“ (podložka a spad po ošetření, ne testy na vzorku včel). */
export function isMiteFall(e: VarroaCheckEvent): boolean {
  return e.method === 'bottom_board' || e.method === 'post_treatment';
}

/** Přirozený spad roztočů za den (jen u podložky). */
export function mitesPerDay(e: VarroaCheckEvent): number | null {
  if (e.method !== 'bottom_board' || !e.days || e.days <= 0) return null;
  return e.miteCount / e.days;
}

/** Procento napadení u testů na vzorku včel. */
export function infestationPercent(e: VarroaCheckEvent): number | null {
  if (e.method !== 'alcohol_wash' && e.method !== 'sugar_roll') return null;
  if (!e.sampleSize || e.sampleSize <= 0) return null;
  return (e.miteCount / e.sampleSize) * 100;
}

/** Datum, do kdy u včelstva běží ochranná lhůta po daném ošetření. */
export function withdrawalUntil(t: TreatmentEvent): ISODate | null {
  if (t.withdrawalDays == null || t.withdrawalDays <= 0) return null;
  return addDays(t.endDate ?? t.date, t.withdrawalDays);
}

export interface WithdrawalConflict {
  colonyId: ID;
  treatmentId: ID;
  productName: string;
  until: ISODate;
}

/**
 * Kontrola před uložením medobraní: nevytáčí se med z včelstva,
 * u kterého ještě běží ochranná lhůta? Vrací seznam konfliktů
 * (prázdný = v pořádku). Aplikace je zobrazí jako varování.
 */
export function checkWithdrawal(
  harvest: Pick<HarvestEvent, 'date' | 'colonyIds'>,
  events: ColonyEvent[],
): WithdrawalConflict[] {
  const conflicts: WithdrawalConflict[] = [];
  for (const e of events) {
    if (e.type !== 'treatment' || e.deletedAt) continue;
    const until = withdrawalUntil(e);
    if (!until) continue;
    if (e.date > harvest.date || harvest.date > until) continue;
    for (const colonyId of harvest.colonyIds) {
      if (e.colonyIds.includes(colonyId)) {
        conflicts.push({ colonyId, treatmentId: e.id, productName: e.productName, until });
      }
    }
  }
  return conflicts;
}

/** Nejstarší přípustné datum zápisu – chrání před překlepem v roce (např. 0026 místo 2026). */
export const MIN_DATE = '1950-01-01';

/** Existující kalendářní den ve tvaru `YYYY-MM-DD` (ne 2026-02-30 ani 2026-13-01). */
export function isValidDate(date: unknown): date is ISODate {
  if (typeof date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
  const [y, m, d] = date.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
}

/** Kontrola data `YYYY-MM-DD`: platný den, ne v budoucnosti, ne v daleké minulosti. */
export function dateProblems(date: unknown, label = 'Datum', allowFuture = false): string[] {
  if (!isValidDate(date)) return [`${label}: neplatné datum.`];
  if (!allowFuture && date > todayISO()) return [`${label} nemůže být v budoucnosti.`];
  if (date < MIN_DATE) return [`${label} je příliš daleko v minulosti – zkontrolujte rok.`];
  return [];
}

/** Základní kontrola konzistence události před uložením. */
export function validateEvent(e: ColonyEvent): string[] {
  const errors: string[] = [];
  errors.push(...dateProblems(e.date));
  if (e.colonyIds.length === 0) errors.push('Vyberte alespoň jedno včelstvo.');
  // čísla napsaná do pole: žádné nesmysly (záporné, obří, nečíselné)
  const num = (v: unknown, label: string, min: number, max: number) => {
    if (v == null) return;
    if (typeof v !== 'number' || !Number.isFinite(v)) errors.push(`${label}: neplatné číslo.`);
    else if (v < min) errors.push(min === 0 ? `${label} nemůže být záporné.` : `${label}: nejméně ${min}.`);
    else if (v > max) errors.push(`${label}: příliš velké číslo (nejvýš ${max.toLocaleString('cs-CZ')}).`);
  };
  num(e.temperatureC, 'Teplota', -40, 60);
  switch (e.type) {
    case 'inspection':
      num(e.occupiedSeams, 'Obsazené uličky', 0, 100);
      num(e.broodFrames, 'Plásty s plodem', 0, 100);
      num(e.storesKg, 'Zásoby', 0, 500);
      num(e.boxCountAfter, 'Počet plodišťových nástavků', 0, 20);
      break;
    case 'treatment':
      if (!e.productName?.trim()) errors.push('Chybí přípravek.');
      num(e.withdrawalDays, 'Ochranná lhůta', 0, 3650);
      num(e.seriesIndex, 'Pořadí v sérii', 1, 100);
      num(e.seriesTotal, 'Celkem v sérii', 1, 100);
      if (e.seriesIndex != null && e.seriesTotal != null && e.seriesIndex > e.seriesTotal) errors.push('Pořadí v sérii je větší než celkový počet.');
      // konec léčení může být naplánovaný dopředu (vyjmutí pásků za několik týdnů)
      if (e.endDate) errors.push(...dateProblems(e.endDate, 'Konec léčení', true));
      if (e.endDate && isValidDate(e.endDate) && isValidDate(e.date) && e.endDate > addDays(e.date, 366)) errors.push('Konec léčení je víc než rok po začátku.');
      if (!(e.dosePerColony != null && Number.isFinite(e.dosePerColony) && e.dosePerColony > 0)) errors.push('Zadejte dávku na včelstvo (patří do evidence léčiv).');
      else num(e.dosePerColony, 'Dávka', 0, 100000);
      if (e.endDate && e.endDate < e.date) errors.push('Konec léčení je před začátkem.');
      break;
    case 'harvest':
      if (!(e.honeyKg > 0)) errors.push('Množství medu musí být kladné.');
      else num(e.honeyKg, 'Množství medu', 0, 100000);
      num(e.framesExtracted, 'Vytočeno plástů', 0, 100000);
      if (e.perColonyKg) {
        const values = Object.values(e.perColonyKg);
        const sum = values.reduce((a, b) => a + b, 0);
        if (values.some((v) => typeof v !== 'number' || !Number.isFinite(v) || v < 0)) errors.push('Med po včelstvech nemůže být záporný.');
        else if (Math.abs(sum - e.honeyKg) > 0.05) {
          errors.push('Součet po včelstvech nesouhlasí s celkovým množstvím.');
        }
      }
      if (e.waterPercent != null && !(e.waterPercent >= 10 && e.waterPercent <= 30)) {
        errors.push('Obsah vody mimo rozumný rozsah (10–30 %).');
      }
      break;
    case 'feeding':
      if (!(e.amountPerColony > 0)) errors.push('Množství krmiva musí být kladné.');
      else num(e.amountPerColony, 'Množství krmiva na včelstvo', 0, 100);
      break;
    case 'varroa_check':
      if (e.method === 'winter_debris' && (e.miteCount == null || Number.isNaN(e.miteCount))) {
        /* výsledek vyšetření měli se doplní později */
      } else if (e.miteCount == null || Number.isNaN(e.miteCount)) errors.push('Zadejte počet spadlých roztočů (i když je to 0).');
      else if (e.miteCount < 0) errors.push('Počet roztočů nemůže být záporný.');
      else num(e.miteCount, 'Počet roztočů', 0, 1000000);
      num(e.days, 'Dní na podložce', 1, 365);
      num(e.sampleSize, 'Včel ve vzorku', 1, 100000);
      break;
    case 'move':
      // bez cíle by včelstvo zmizelo ze všech stanovišť
      if (!e.toApiaryId) errors.push('Vyberte cílové stanoviště.');
      else if (e.fromApiaryId === e.toApiaryId) errors.push('Cílové stanoviště je stejné jako výchozí.');
      break;
    case 'super_change':
      if (!(e.count >= 1)) errors.push('Počet medníků musí být alespoň 1.');
      else if (!Number.isInteger(e.count) || e.count > 20) errors.push('Počet medníků musí být celé číslo (nejvýš 20).');
      break;
    case 'split':
      if (e.colonyIds.length !== 1) errors.push('Oddělek se dělá z právě jednoho včelstva.');
      break;
  }
  return errors;
}

/**
 * Rozdělí zápis pro více včelstev na samostatné záznamy (jeden na včelstvo).
 * Medobraní zůstává společné – celkové kilogramy se zadávají za skupinu.
 * Používá se při ukládání i při migraci starších dat (schéma 1 → 2).
 */
export function splitPerColony(e: ColonyEvent): ColonyEvent[] {
  if (e.colonyIds.length <= 1 || e.type === 'harvest') return [e];
  const batchId = e.batchId ?? e.id;
  return e.colonyIds.map((colonyId, i) => ({
    ...e,
    id: i === 0 ? e.id : crypto.randomUUID(),
    colonyIds: [colonyId],
    batchId,
  }));
}

/**
 * Je zápis nejnovější svého druhu u včelstva? Jen takový smí měnit aktuální
 * stav včelstva (počet nástavků, stanoviště, stav) – oprava starého zápisu
 * nebo dodatečně zapsaná starší prohlídka nesmí přepsat novější údaje.
 */
export function isLatestOfKind(event: ColonyEvent, others: ColonyEvent[], same: (e: ColonyEvent) => boolean): boolean {
  return !others.some(
    (o) => o.id !== event.id && !o.deletedAt && same(o) && (o.date > event.date || (o.date === event.date && o.createdAt > event.createdAt)),
  );
}

/** Odstraní klíče s hodnotou undefined (smazané pole má zmizet i z uloženého záznamu). */
export function dropUndefined<T extends object>(o: T): T {
  for (const k of Object.keys(o) as (keyof T)[]) if (o[k] === undefined) delete o[k];
  return o;
}

/**
 * Promítne nasazení/odebrání medníků do seznamu medníků včelstva.
 * Odebírají se nejdřív nejnověji nasazené medníky daného typu,
 * a když daného typu nejsou, tak jakékoli.
 */
export function applySuperChange(
  supers: import('./model').HoneySuper[] | undefined,
  e: Pick<import('./model').SuperChangeEvent, 'action' | 'superType' | 'count' | 'date'>,
): import('./model').HoneySuper[] {
  const list = [...(supers ?? [])];
  if (e.action === 'add') {
    for (let i = 0; i < e.count; i++) list.push({ id: crypto.randomUUID(), type: e.superType, addedAt: e.date });
    return list;
  }
  for (let i = 0; i < e.count && list.length; i++) {
    let idx = -1;
    for (let j = list.length - 1; j >= 0; j--) if (list[j].type === e.superType) { idx = j; break; }
    list.splice(idx === -1 ? list.length - 1 : idx, 1);
  }
  return list;
}
